import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle, Clock, Shield, FileCheck, Calendar } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ComplianceFinding {
  category: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  description: string;
  affectedRecords?: number;
  dataTypes?: string[];
}

interface ComplianceAudit {
  id: number;
  auditType: 'GDPR' | 'SOC2' | 'DATA_RETENTION' | 'ACCESS_CONTROL';
  status: 'PASS' | 'FAIL' | 'WARNING';
  findings: ComplianceFinding[];
  recommendations: string[];
  executedAt: string;
  executedBy: number;
}

interface ComplianceSchedule {
  id: number;
  auditType: string;
  frequency: string;
  enabled: boolean;
  lastRun: string | null;
  nextRun: string;
  createdAt: string;
}

const CompliancePage = () => {
  const [selectedAuditType, setSelectedAuditType] = useState<string>('ALL');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch compliance audits
  const { data: audits = [], isLoading: auditsLoading } = useQuery<ComplianceAudit[]>({
    queryKey: ['/api/compliance/audits'],
  });

  // Fetch compliance schedules
  const { data: schedules = [], isLoading: schedulesLoading } = useQuery<ComplianceSchedule[]>({
    queryKey: ['/api/compliance/schedule'],
  });

  // Run audit mutation
  const runAuditMutation = useMutation({
    mutationFn: async (auditType: string) => {
      const response = await apiRequest('POST', '/api/compliance/run-audit', { auditType });
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (error) {
        console.error('Failed to parse audit response:', text);
        throw new Error('Invalid response format from audit service');
      }
    },
    onSuccess: () => {
      toast({
        title: "Audit Completed",
        description: "Compliance audit has been successfully executed.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/compliance/audits'] });
    },
    onError: (error: any) => {
      toast({
        title: "Audit Failed",
        description: error.message || "Failed to run compliance audit",
        variant: "destructive",
      });
    },
  });

  // Create schedule mutation
  const createScheduleMutation = useMutation({
    mutationFn: async (scheduleData: { auditType: string; frequency: string }) => {
      const response = await apiRequest('POST', '/api/compliance/schedule', scheduleData);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Schedule Created",
        description: "Compliance audit schedule has been created.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/compliance/schedule'] });
    },
    onError: (error: any) => {
      toast({
        title: "Schedule Creation Failed",
        description: error.message || "Failed to create compliance schedule",
        variant: "destructive",
      });
    },
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PASS':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'WARNING':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'FAIL':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      PASS: 'bg-green-100 text-green-800',
      WARNING: 'bg-yellow-100 text-yellow-800',
      FAIL: 'bg-red-100 text-red-800',
    };
    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const getSeverityBadge = (severity: string) => {
    const variants = {
      LOW: 'bg-blue-100 text-blue-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      HIGH: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800',
    };
    return variants[severity as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Compliance & Audits</h1>
          <p className="text-gray-600">GDPR and SOC 2 compliance monitoring and audit management</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedAuditType} onValueChange={setSelectedAuditType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select audit type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Audits</SelectItem>
              <SelectItem value="GDPR">GDPR Audit</SelectItem>
              <SelectItem value="SOC2">SOC 2 Audit</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => runAuditMutation.mutate(selectedAuditType)}
            disabled={runAuditMutation.isPending}
            className="flex items-center gap-2"
          >
            <Shield className="h-4 w-4" />
            {runAuditMutation.isPending ? 'Running...' : 'Run Audit'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="audits" className="space-y-4">
        <TabsList>
          <TabsTrigger value="audits">Audit Results</TabsTrigger>
          <TabsTrigger value="schedules">Audit Schedules</TabsTrigger>
          <TabsTrigger value="overview">Compliance Overview</TabsTrigger>
        </TabsList>

        <TabsContent value="audits" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent Audit Results</CardTitle>
              <CardDescription>View the latest compliance audit findings and recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              {auditsLoading ? (
                <div className="text-center py-4">Loading audit results...</div>
              ) : audits.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileCheck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No compliance audits have been run yet.</p>
                  <p className="text-sm">Click "Run Audit" to perform your first compliance check.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {audits.map((audit) => (
                    <Card key={audit.id} className="border">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(audit.status)}
                            <div>
                              <h3 className="font-semibold">{audit.auditType} Compliance Audit</h3>
                              <p className="text-sm text-gray-500">
                                {new Date(audit.executedAt).toLocaleDateString()} at {new Date(audit.executedAt).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                          <Badge className={getStatusBadge(audit.status)}>
                            {audit.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {audit.findings.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2">Findings ({audit.findings.length})</h4>
                            <div className="space-y-2">
                              {audit.findings.map((finding, index) => (
                                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                  <Badge className={getSeverityBadge(finding.severity)}>
                                    {finding.severity}
                                  </Badge>
                                  <div className="flex-1">
                                    <p className="font-medium text-sm">{finding.category}</p>
                                    <p className="text-sm text-gray-600">{finding.description}</p>
                                    {finding.affectedRecords && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        Affected records: {finding.affectedRecords}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {audit.recommendations.length > 0 && (
                          <div>
                            <h4 className="font-medium mb-2">Recommendations</h4>
                            <ul className="space-y-1">
                              {audit.recommendations.map((recommendation, index) => (
                                <li key={index} className="text-sm text-gray-600 flex items-start gap-2">
                                  <span className="text-blue-500 mt-1">•</span>
                                  {recommendation}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedules" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Audit Schedules</CardTitle>
              <CardDescription>Configure automated compliance audit schedules</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <Select onValueChange={(value) => {
                    const [auditType, frequency] = value.split('|');
                    createScheduleMutation.mutate({ auditType, frequency });
                  }}>
                    <SelectTrigger className="w-[300px]">
                      <SelectValue placeholder="Create new schedule" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GDPR|weekly">GDPR - Weekly</SelectItem>
                      <SelectItem value="GDPR|monthly">GDPR - Monthly</SelectItem>
                      <SelectItem value="SOC2|monthly">SOC 2 - Monthly</SelectItem>
                      <SelectItem value="SOC2|quarterly">SOC 2 - Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {schedulesLoading ? (
                  <div className="text-center py-4">Loading schedules...</div>
                ) : schedules.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No audit schedules configured.</p>
                    <p className="text-sm">Create a schedule to automate compliance audits.</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {schedules.map((schedule) => (
                      <Card key={schedule.id} className="border">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <h3 className="font-semibold">{schedule.auditType} Audit</h3>
                              <p className="text-sm text-gray-500">
                                Frequency: {schedule.frequency} | 
                                Next run: {new Date(schedule.nextRun).toLocaleDateString()}
                              </p>
                              {schedule.lastRun && (
                                <p className="text-xs text-gray-400">
                                  Last run: {new Date(schedule.lastRun).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                            <Badge variant={schedule.enabled ? "default" : "secondary"}>
                              {schedule.enabled ? "Active" : "Disabled"}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">GDPR Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Data Retention</span>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">User Consent</span>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Data Portability</span>
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">SOC 2 Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Security Controls</span>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Availability</span>
                    <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Processing Integrity</span>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="font-medium">Last GDPR Audit:</span>
                    <br />
                    <span className="text-gray-500">
                      {audits.find(a => a.auditType === 'GDPR') 
                        ? new Date(audits.find(a => a.auditType === 'GDPR')!.executedAt).toLocaleDateString()
                        : 'Never'
                      }
                    </span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Last SOC 2 Audit:</span>
                    <br />
                    <span className="text-gray-500">
                      {audits.find(a => a.auditType === 'SOC2')
                        ? new Date(audits.find(a => a.auditType === 'SOC2')!.executedAt).toLocaleDateString()
                        : 'Never'
                      }
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompliancePage;